import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const DEFAULT_SALESFORCE_OAUTH_CALLBACK_URL =
  'https://app.chattitude.ai/slickbots-ws/api/integrations/oauth/callback';

/** Callback URL shown for Salesforce Connected App (matches OAuthIntegrationService redirect). */
function salesforceOAuthCallbackDisplayUrl(resellerBaseUrl) {
  if (typeof window !== 'undefined' && window.location?.host?.includes('localhost')) {
    return 'http://localhost:8080/api/integrations/oauth/callback';
  }
  const t = (resellerBaseUrl || '').trim().replace(/\/+$/, '');
  if (!t) return DEFAULT_SALESFORCE_OAUTH_CALLBACK_URL;
  return `${t}/slickbots-ws/api/integrations/oauth/callback`;
}

const SalesforceModal = ({ open, onClose, productName = 'Chattitude', resellerBaseUrl }) => {
  const [credentials, setCredentials] = useState({ clientId: '', clientSecret: '' });
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  const oauthCallbackDisplayUrl = useMemo(
    () => salesforceOAuthCallbackDisplayUrl(resellerBaseUrl),
    [resellerBaseUrl]
  );

  const authHeader = () => {
    const jwtToken = Cookies.getJwtToken();
    if (jwtToken) return { Authorization: `Bearer ${jwtToken}` };
    // Fallback: backend supports direct tenant-id in Authorization for testing
    const tenantId = Cookies.getTenantId();
    return tenantId ? { Authorization: String(tenantId) } : {};
  };

  useEffect(() => {
    const fetchCredentials = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/integrations/salesforce/credentials`, { headers: authHeader() });
        setCredentials({
          clientId: res.data?.clientId || '',
          clientSecret: res.data?.clientSecret || '',
        });
      } catch (e) {
        setCredentials({ clientId: '', clientSecret: '' });
        setError('Failed to fetch credentials.');
      } finally {
        setLoading(false);
      }
    };

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/integrations/salesforce/status`, { headers: authHeader() });
        setConnected(!!res.data?.connected);
      } catch (e) {
        setConnected(false);
      }
    };

    if (open) {
      fetchCredentials();
      fetchStatus();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!credentials.clientId) return 'Client ID (Consumer Key) is required.';
    if (!credentials.clientSecret) return 'Client Secret (Consumer Secret) is required.';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/integrations/salesforce/credentials`, credentials, { headers: authHeader() });
      setSnackbar({ open: true, message: 'Credentials saved successfully!', severity: 'success' });
      setTimeout(() => onClose && onClose(), 1200);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save credentials.', severity: 'error' });
      setError('Failed to save credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectSalesforce = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      // Save creds first
      await axios.post(`${API_URL}/integrations/salesforce/credentials`, credentials, { headers: authHeader() });

      const res = await axios.get(`${API_URL}/integrations/salesforce/authorize-url`, { headers: authHeader() });
      const url = res.data?.url;
      if (!url) throw new Error('Missing authorize URL');

      window.open(url, '_blank');

      const poll = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API_URL}/integrations/salesforce/status`, { headers: authHeader() });
          if (statusRes.data?.connected) {
            setConnected(true);
            setConnecting(false);
            clearInterval(poll);
            setSnackbar({ open: true, message: 'Salesforce connected!', severity: 'success' });
          }
        } catch (e) {
          // ignore while polling
        }
      }, 2000);
      setPollInterval(poll);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to initiate Salesforce OAuth.', severity: 'error' });
      setError('Failed to initiate Salesforce OAuth.');
      setConnecting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ zIndex: 99999 }}
      PaperProps={{ style: { minWidth: 1200, minHeight: 800, zIndex: 99999 } }}
    >
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        Salesforce Credentials
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Client ID (Consumer Key)"
              name="clientId"
              value={credentials.clientId}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Client Secret (Consumer Secret)"
              name="clientSecret"
              value={credentials.clientSecret}
              onChange={handleChange}
              required
              fullWidth
              type="password"
            />

            <Box sx={{ mt: 3, mb: 2, p: 2, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Salesforce Integration Instructions
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                For the Salesforce integration you will need to create a Connected App in Salesforce and obtain your Consumer Key (Client ID) and Consumer Secret (Client Secret). Below are the steps:
              </Typography>
              <ol style={{ paddingLeft: 18, marginBottom: 8 }}>
                <li>Log in to your Salesforce org and go to Setup (gear icon → Setup).</li>
                <li>In the Quick Find box, search for &quot;App Manager&quot; and click it.</li>
                <li>Click &quot;New Connected App&quot; (or &quot;Create&quot; → &quot;Connected App&quot; for older orgs).</li>
                <li>{`Enable "Allow OAuth Settings" and fill in the required fields, including a connected app name (e.g. ${productName}):`}</li>
                <li>
                  In the OAuth settings section, configure:
                  <ul style={{ marginTop: 4 }}>
                    <li><strong>Callback URL</strong> - {oauthCallbackDisplayUrl}</li>
                    <li><strong>Selected OAuth Scopes</strong> - Add: &quot;api&quot;, &quot;refresh_token&quot;, and &quot;full&quot; (or &quot;Access and manage your data (api)&quot;, &quot;Perform requests at any time (refresh_token)&quot;, &quot;Full access (full)&quot;)</li>
                  </ul>
                </li>
                <li>Save the Connected App. After saving, you will see the Consumer Key (Client ID) and Consumer Secret. Click &quot;Click to reveal&quot; next to Consumer Secret to view it.</li>
                <li>Copy the Consumer Key and Consumer Secret and paste them in the form fields above.</li>
                <li>Click &quot;Save&quot; to store credentials, then &quot;Connect to Salesforce&quot; to authorize and obtain the access token.</li>
              </ol>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555', mt: 1 }}>
                Note: For sandbox/developer orgs, use https://test.salesforce.com when creating the Connected App. The OAuth endpoints will use the production (login.salesforce.com) or sandbox (test.salesforce.com) based on your app configuration.
              </Typography>
            </Box>

            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 2, px: 3 }}>
        <Button onClick={handleSave} variant="outlined" color="primary" disabled={loading || connecting} sx={{ minWidth: 180, py: 1.2 }}>
          SAVE &amp; Close
        </Button>
        <Button
          onClick={handleConnectSalesforce}
          variant="contained"
          color="success"
          disabled={connecting || loading || !credentials.clientId || !credentials.clientSecret}
          sx={{ minWidth: 220, py: 1.2 }}
        >
          {connected ? 'Connected to Salesforce' : connecting ? 'Connecting...' : 'Connect to Salesforce'}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default SalesforceModal;
